import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { apiClient } from '../../services/authService';
import sessionService from '../../services/sessionService';
import { formatMoney } from '../../utils/money';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Calendar,
  Plus,
  Search,
  Filter,
  Eye,
  Receipt,
  Package,
  CheckCircle,
  Clock,
  XCircle,
  User,
  Users as UsersIcon,
  Euro,
  Edit,
  Trash2,
  MoreVertical,
  X,
  List,
  LayoutGrid,
  Archive,
  CircleDollarSign
} from 'lucide-react';
import './SessionsPage.css';

const RejectReasonToast = ({
  title,
  label,
  placeholder,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
}) => {
  const [value, setValue] = useState('');

  return (
    <div>
      <div className="toast-title">{title}</div>
      <div>
        <div>{label}</div>
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          rows={4}
          autoFocus
        />
      </div>
      <div className="toast-actions">
        <button
          type="button"
          className="toast-btn toast-btn-danger"
          onClick={() => onConfirm(value)}
          disabled={!value.trim()}
        >
          {confirmText}
        </button>
        <button type="button" className="toast-btn" onClick={onCancel}>
          {cancelText}
        </button>
      </div>
    </div>
  );
};

const SessionsPage = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const { user, isAdmin, isResponsable, isLogistiqueCoordinator, isManager } = useAuth();
  const [showArchived, setShowArchived] = useState(false);
  const allCentresLabel = i18n.exists('common.allCentres') ? t('common.allCentres') : 'Tous les centres';
  const [sessions, setSessions] = useState([]);
  const [restockOrders, setRestockOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { canCreate, canEdit, canDelete, canValidate, canReject } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [listType, setListType] = useState('sessions');
  const [centres, setCentres] = useState([]);
  const availableCentres = isAdmin
    ? centres
    : (Array.isArray(user?.centres_details) ? user.centres_details : []);
  const hasMultipleCentres = availableCentres.length > 1;
  const hasSingleCentre = availableCentres.length === 1;
  const [selectedCentre, setSelectedCentre] = useState('');

  const getCurrency = (session) => {
    return session?.centre_devise || user?.centres_details?.[0]?.devise || 'EUR';
  };
  const toMoney = (value, session) => formatMoney(value, { locale: i18n.language, currency: getCurrency(session) });
  const [visibleStatuses, setVisibleStatuses] = useState(null);
  const [activeMobileStatus, setActiveMobileStatus] = useState(isResponsable ? 'VERIFICATION' : 'EN_ATTENTE');
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem('sessions.viewMode') || 'list';
    } catch (e) {
      return 'list';
    }
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    fetchSessions(currentPage);
  }, [currentPage, searchTerm, filterStatus, selectedCentre, showArchived, listType]);

  useEffect(() => {
    if (hasSingleCentre && !selectedCentre) {
      setSelectedCentre(availableCentres[0]?.id || '');
    }
  }, [hasSingleCentre, availableCentres, selectedCentre]);

  useEffect(() => {
    const fetchCentres = async () => {
      if (!isAdmin) return;
      try {
        const res = await apiClient.get('/centres/');
        const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setCentres(data);
      } catch (e) {
        setCentres([]);
      }
    };
    fetchCentres();
  }, [isAdmin]);

  useEffect(() => {
    const fetchVisibleStatuses = async () => {
      try {
        const res = await apiClient.get('/auth/session_visible_statuses/');
        setVisibleStatuses(res.data?.statuses || []);
      } catch (e) {
        setVisibleStatuses(null);
      }
    };

    fetchVisibleStatuses();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('sessions.viewMode', viewMode);
    } catch (e) {
      // ignore
    }
  }, [viewMode]);


  const fetchSessions = async (page = 1) => {
    try {
      setLoading(true);
      const shouldFetchSessions = listType === 'sessions' || listType === 'all';
      const shouldFetchRestocks = listType === 'restock' || listType === 'all';

      if (shouldFetchSessions) {
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('page_size', pageSize);

        if (searchTerm) {
          params.append('search', searchTerm);
        }
        if (filterStatus !== 'all') {
          params.append('status', filterStatus);
        }
        if (selectedCentre) {
          params.append('centre', selectedCentre);
        }
        
        // Ajouter le filtre pour les archives (uniquement pour les admins)
        if (isAdmin && showArchived) {
          params.append('include_archived', 'true');
        } else if (!showArchived) {
          params.append('is_active', 'true');
        }

        const response = await apiClient.get(`/sessions/?${params.toString()}`);

        if (response.data?.results) {
          setSessions(response.data.results);
          setTotalCount(response.data.count || 0);
          setTotalPages(Math.max(1, Math.ceil((response.data.count || 0) / pageSize)));
        } else {
          const data = response.data || [];
          setSessions(data);
          setTotalCount(data.length);
          setTotalPages(1);
        }
      } else {
        setSessions([]);
        setTotalCount(0);
        setTotalPages(1);
      }

      if (shouldFetchRestocks) {
        const restockStatus = isLogistiqueCoordinator ? 'PENDING_LOGISTICS' : isManager ? 'PENDING_MANAGER' : '';
        const restockResponse = await sessionService.supplierOrders({
          order_type_group: 'STOCK_RESTOCK',
          status: restockStatus,
          centre_id: selectedCentre,
        });
        const rows = restockResponse?.results || [];
        const search = searchTerm.trim().toLowerCase();
        setRestockOrders(search ? rows.filter((order) => {
          const text = [
            order.supplier_name,
            order.centre_name,
            order.created_by_name,
            order.status_display,
            ...(order.lines || []).map((line) => line.consommable_name),
          ].filter(Boolean).join(' ').toLowerCase();
          return text.includes(search);
        }) : rows);
      } else {
        setRestockOrders([]);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSessions = sessions;
  const canEditSession = canEdit('sessions');
  const canCancelRequestedSession = canDelete('sessions');
  const canDragDrop = (isLogistiqueCoordinator && canValidate('sessions')) || (!isResponsable && canEdit('sessions')) || (isResponsable && canCancelRequestedSession);

  const columns = [
    { id: 'VERIFICATION', label: t('status.session.VERIFICATION'), color: '#3b82f6', icon: Clock },
    { id: 'ANNULEE', label: t('status.session.ANNULEE'), color: '#6b7280', icon: XCircle },
    { id: 'DEMANDE_MODIF', label: t('status.session.DEMANDE_MODIF'), color: '#f97316', icon: Clock },
    { id: 'DEMANDE_MODIF_MANAGER', label: t('status.session.DEMANDE_MODIF_MANAGER'), color: '#fb923c', icon: Clock },
    { id: 'VERIFIE', label: t('status.session.VERIFIE'), color: '#22c55e', icon: CheckCircle },
    { id: 'EN_ATTENTE', label: t('status.session.EN_ATTENTE'), color: '#f59e0b', icon: Clock },
    { id: 'VALIDEE', label: t('status.session.VALIDEE'), color: '#76b82a', icon: CheckCircle },
    { id: 'REFUSEE', label: t('status.session.REFUSEE'), color: '#ef4444', icon: XCircle }
  ];

  const sessionsByStatus = columns.reduce((acc, col) => {
    acc[col.id] = filteredSessions.filter(s => s.status === col.id);
    return acc;
  }, {});

  const mobileSessions = sessionsByStatus[activeMobileStatus] || [];

  const handleEditClick = (session) => {
    navigate(`/sessions/${session.id}/edit`);
  };

  const getInstructorName = (session) => (
    session?.instructor_details?.full_name
    || session?.instructor_name
    || '-'
  );

  const getCreatorName = (session) => (
    session?.created_by_name
    || session?.trainer_name
    || '-'
  );

  const getStatusClass = (status) => {
    switch (status) {
      case 'PENDING_LOGISTICS':
        return 'status-pending';
      case 'PENDING_MANAGER':
        return 'status-approved';
      case 'VERIFICATION':
        return 'status-pending';
      case 'VERIFIE':
        return 'status-approved';
      case 'DEMANDE_MODIF':
        return 'status-pending';
      case 'DEMANDE_MODIF_MANAGER':
        return 'status-pending';
      case 'EN_ATTENTE':
        return 'status-pending';
      case 'VALIDEE':
        return 'status-validated';
      case 'REFUSEE':
        return 'status-rejected';
      case 'ANNULEE':
        return 'status-cancelled';
      default:
        return 'status-pending';
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getOrderStatusLabel = (status, fallback = '') => (
    fallback || t(`demandes.status.${status}`, { defaultValue: status || '-' })
  );

  const getAllowedRestockNextStatuses = (order) => {
    if (order?.status === 'PENDING_LOGISTICS') {
      return (isLogistiqueCoordinator || isAdmin) ? ['PENDING_MANAGER', 'CANCELLED'] : [];
    }
    if (order?.status === 'PENDING_MANAGER') {
      return (isManager || isAdmin) ? ['NEW', 'CANCELLED'] : [];
    }
    return [];
  };

  const handleRestockStatusChange = async (order, newStatus) => {
    if (!order?.order_id || !newStatus || newStatus === order.status) return;

    try {
      await sessionService.updateSupplierOrderStatus(order.order_id, newStatus);
      setRestockOrders((prev) => prev.map((row) => (
        row.order_id === order.order_id
          ? { ...row, status: newStatus, status_display: getOrderStatusLabel(newStatus) }
          : row
      )));
      toast.success(t('demandes.toasts.statusUpdated'));
    } catch (error) {
      toast.error(t('demandes.toasts.statusUpdateError'));
    }
  };

  const handleDeleteSession = async (sessionId) => {
    // Confirmation toast keeps the user inside the app.
    const toastId = toast.custom(
      'danger',
      <div style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>
          {t('sessions.deleteConfirmTitle')}
        </div>
        <div style={{ marginBottom: '1rem', color: '#6b7280', fontSize: '0.9rem' }}>
          {t('sessions.deleteConfirmMessage')}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1rem' }}>
          <button
            type="button"
            className="toast-btn toast-btn-danger"
            onClick={() => {
              toast.remove(toastId);
              proceedWithSessionDeletion(sessionId);
            }}
          >
            {t('common.delete')}
          </button>
          <button 
            type="button" 
            className="toast-btn" 
            onClick={() => toast.remove(toastId)}
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>,
      { duration: null }
    );
    
    const proceedWithSessionDeletion = async (sessionId) => {
      try {
        await sessionService.delete(sessionId);
        fetchSessions(currentPage);
        toast.success(t('sessions.deleteSuccess'));
      } catch (error) {
        console.error('Error deleting session:', error);
        toast.error(t('sessions.deleteError'));
      }
    };
  };

  const handleCancelSession = async (sessionId) => {
    const ok = await confirm(t('sessions.confirmCancel') || t('common.confirm'), {
      title: t('sessions.confirmTitle'),
      confirmText: t('common.confirm'),
      cancelText: t('common.cancel'),
      variant: 'danger',
    });
    if (!ok) return;

    updateSessionStatusOptimistic(sessionId, 'ANNULEE');
    try {
      await apiClient.post(`/sessions/${sessionId}/cancel/`);
      fetchSessions(currentPage);
      toast.success(t('status.session.ANNULEE'));
    } catch (e) {
      fetchSessions(currentPage);
      toast.error(t('sessions.detail.errors.statusChange'));
    }
  };

  const updateSessionStatusOptimistic = (sessionId, nextStatus) => {
    setSessions((prev) => prev.map((s) => (s.id === sessionId ? { ...s, status: nextStatus } : s)));
  };

  const persistStatusChange = async ({ sessionId, fromStatus, toStatus }) => {
    if (fromStatus === toStatus) return;

    if (toStatus === 'VERIFICATION') {
      await sessionService.reopenToVerification(sessionId);
      return;
    }

    if (toStatus === 'VALIDEE') {
      await apiClient.post(`/sessions/${sessionId}/validate/`);
      return;
    }

    if (toStatus === 'EN_ATTENTE') {
      await apiClient.post(`/sessions/${sessionId}/reset/`);
      return;
    }

    if (toStatus === 'ANNULEE') {
      await apiClient.post(`/sessions/${sessionId}/cancel/`);
      return;
    }

    if (toStatus === 'VERIFIE') {
      await sessionService.markVerified(sessionId);
      return;
    }

    if (toStatus === 'REFUSEE') {
      throw new Error('REJECT_REASON_REQUIRED');
      return;
    }

    const err = new Error(`Unsupported status transition to ${toStatus}`);
    err.code = 'UNSUPPORTED';
    throw err;
  };

  const handleDragEnd = async (result) => {
    if (!canDragDrop) return;

    const { draggableId, destination } = result;
    if (!destination) return;

    const toStatus = destination.droppableId;
    if (isResponsable) {
      if (!['ANNULEE', 'VERIFICATION'].includes(toStatus)) return;
    } else if (isAdmin) {
      if (!['DEMANDE_MODIF', 'VERIFIE', 'EN_ATTENTE', 'VALIDEE', 'REFUSEE', 'ANNULEE'].includes(toStatus)) return;
    } else if (isLogistiqueCoordinator) {
      if (!['DEMANDE_MODIF', 'VERIFIE', 'ANNULEE'].includes(toStatus)) return;
    } else {
      if (!['EN_ATTENTE', 'VALIDEE', 'REFUSEE', 'ANNULEE'].includes(toStatus)) return;
    }

    const session = sessions.find((s) => s.id === draggableId);
    if (!session) return;

    const fromStatus = session.status;
    if (fromStatus === toStatus) return;

    if (isResponsable) {
      if (toStatus === 'VERIFICATION' && fromStatus !== 'ANNULEE') return;
      if (toStatus === 'ANNULEE' && ['ANNULEE', 'VALIDEE', 'REFUSEE'].includes(fromStatus)) return;
    }

    if (isLogistiqueCoordinator) {
      if (fromStatus !== 'VERIFICATION') return;
    }

    if (isAdmin && ['VERIFIE', 'DEMANDE_MODIF'].includes(toStatus) && fromStatus !== 'VERIFICATION') {
      return;
    }

    if (toStatus === 'REFUSEE') {
      if (isResponsable) return;
      const toastId = toast.custom(
        'info',
        <RejectReasonToast
          title={t('sessions.detail.decision.reject')}
          label={t('sessions.detail.rejectForm.label')}
          placeholder={t('sessions.detail.rejectForm.placeholder')}
          confirmText={t('sessions.detail.rejectForm.confirm')}
          cancelText={t('common.cancel')}
          onConfirm={async (reason) => {
            toast.remove(toastId);
            updateSessionStatusOptimistic(draggableId, 'REFUSEE');
            try {
              await apiClient.post(`/sessions/${draggableId}/reject/`, { reason: reason.trim() });
              fetchSessions(currentPage);
              toast.success(t('sessions.detail.decision.reject'));
            } catch (e) {
              updateSessionStatusOptimistic(draggableId, fromStatus);
              toast.error(t('sessions.detail.errors.reject'));
            }
          }}
          onCancel={() => {
            toast.remove(toastId);
          }}
        />,
        { duration: null }
      );
      return;
    }

    if (toStatus === 'DEMANDE_MODIF') {
      if (isResponsable) return;
      if (!isLogistiqueCoordinator && !isAdmin) return;

      const toastId = toast.custom(
        'info',
        <RejectReasonToast
          title={t('sessions.detail.actions.requestModification')}
          label={t('sessions.detail.requestModificationForm.label')}
          placeholder={t('sessions.detail.requestModificationForm.placeholder')}
          confirmText={t('common.confirm')}
          cancelText={t('common.cancel')}
          onConfirm={async (note) => {
            toast.remove(toastId);
            updateSessionStatusOptimistic(draggableId, 'DEMANDE_MODIF');
            try {
              await sessionService.requestModification(draggableId, (note || '').trim());
              fetchSessions(currentPage);
              toast.success(t('status.session.DEMANDE_MODIF'));
            } catch (e) {
              updateSessionStatusOptimistic(draggableId, fromStatus);
              toast.error(t('sessions.detail.errors.statusChange'));
            }
          }}
          onCancel={() => {
            toast.remove(toastId);
          }}
        />,
        { duration: null }
      );
      return;
    }

    updateSessionStatusOptimistic(draggableId, toStatus);
    try {
      await persistStatusChange({ sessionId: draggableId, fromStatus, toStatus });
      fetchSessions(currentPage);
      if (toStatus === 'ANNULEE') toast.success(t('status.session.ANNULEE'));
      if (toStatus === 'VERIFIE') toast.success(t('status.session.VERIFIE'));
    } catch (e) {
      updateSessionStatusOptimistic(draggableId, fromStatus);
      toast.error(t('sessions.detail.errors.statusChange'));
    }
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loader"></div>
        <span>{t('sessions.loading')}</span>
      </div>
    );
  }

  return (
    <div className="sessions-page">
      <div className="page-header-section">
        <div className="header-titles">
          <h1 className="page-title">
            <Calendar size={32} />
            {t('sessions.title')}
          </h1>
          <p className="page-subtitle">{t('sessions.subtitle')}</p>
        </div>
        {canCreate('sessions') && (
          <Link to="/sessions/new" className="btn-add">
            <Plus size={20} />
            {t('sessions.new')}
          </Link>
        )}
      </div>

      <div className="filters-section">
        <div className="search-bar">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder={t('sessions.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="search-input"
          />
          {searchTerm && (
            <button 
              className="search-clear-btn"
              onClick={handleClearSearch}
              title={t('common.clearSearch')}
            >
              <X size={16} />
            </button>
          )}
        </div>

        {(isLogistiqueCoordinator || isManager || isAdmin) && (
          <div className="filter-group">
            <Filter size={18} />
            <select
              value={listType}
              onChange={(e) => {
                setListType(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
            >
              <option value="sessions">Sessions</option>
              <option value="restock">Restock</option>
              <option value="all">Sessions + Restock</option>
            </select>
          </div>
        )}

        {(isAdmin ? availableCentres.length >= 1 : hasMultipleCentres) && (
          <div className="filter-group">
            <Filter size={18} />
            <select
              value={selectedCentre}
              onChange={(e) => {
                setSelectedCentre(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
              disabled={availableCentres.length <= 1}
            >
              <option value="">{allCentresLabel}</option>
              {availableCentres.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {listType !== 'restock' && (
        <div className="filter-group">
          <Filter size={18} />
          <select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="all">{t('common.allStatuses')}</option>
            {(
              visibleStatuses || [
                'VERIFICATION',
                'VERIFIE',
                'DEMANDE_MODIF',
                'DEMANDE_MODIF_MANAGER',
                'EN_ATTENTE',
                'VALIDEE',
                'REFUSEE',
                'ANNULEE',
              ]
            ).map((st) => (
              <option key={st} value={st}>{t(`status.session.${st}`)}</option>
            ))}
          </select>
        </div>
        )}

        {isAdmin && (
          <div className="filter-group">
            <button
              className={`filter-toggle-btn ${showArchived ? 'active' : ''}`}
              onClick={() => {
                setShowArchived(!showArchived);
                setCurrentPage(1);
              }}
              title={showArchived ? 'Masquer les archives' : 'Afficher les archives'}
            >
              <Archive size={16} />
              <span>{showArchived ? 'Archives' : 'Actives'}</span>
            </button>
          </div>
        )}

        {listType !== 'restock' && (
        <div className="view-toggle" role="tablist" aria-label={t('common.view') || 'View'}>
          <button
            type="button"
            className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            aria-pressed={viewMode === 'list'}
          >
            <List size={16} />
            <span>{t('common.list') || 'Liste'}</span>
          </button>
          <button
            type="button"
            className={`view-toggle-btn ${viewMode === 'kanban' ? 'active' : ''}`}
            onClick={() => setViewMode('kanban')}
            aria-pressed={viewMode === 'kanban'}
          >
            <LayoutGrid size={16} />
            <span>{t('common.kanban') || 'Kanban'}</span>
          </button>
        </div>
        )}
      </div>

      {(listType === 'restock' || listType === 'all') && (
        <div className="table-container restock-orders-table">
          <table className="data-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>{t('demandes.table.supplier')}</th>
                <th>Centre</th>
                <th>{t('demandes.table.requestDate')}</th>
                <th>{t('demandes.table.quote')}</th>
                <th>{t('demandes.table.supplies')}</th>
                <th>{t('sessions.table.status')}</th>
                <th className="actions-column">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {restockOrders.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>
                    Aucune demande de réapprovisionnement.
                  </td>
                </tr>
              ) : (
                restockOrders.map((order) => (
                  <tr key={order.order_id}>
                    <td>{order.order_type_label || t('demandes.orderTypes.stockRestock')}</td>
                    <td className="name-cell">{order.supplier_name || '-'}</td>
                    <td>{order.centre_name || '-'}</td>
                    <td>{order.created_at ? new Date(order.created_at).toLocaleDateString(i18n.language) : '-'}</td>
                    <td>{toMoney(order.total_devis || 0, { centre_devise: order.supplier_devise || order.centre_devise })}</td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        {(order.lines || []).slice(0, 4).map((line) => (
                          <div key={line.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <Package size={14} />
                            <span>{line.consommable_name}</span>
                            <strong>× {line.quantity}</strong>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusClass(order.status)}`}>
                        {getOrderStatusLabel(order.status, order.status_display)}
                      </span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <Link to={`/mes-demandes/${order.order_id}`} className="action-btn" title={t('common.view')}>
                          <Eye size={16} />
                        </Link>
                        {getAllowedRestockNextStatuses(order).map((status) => (
                          <button
                            key={status}
                            type="button"
                            className={`action-btn ${status === 'CANCELLED' ? 'action-delete' : ''}`}
                            onClick={() => handleRestockStatusChange(order, status)}
                            title={status === 'CANCELLED' ? t('demandes.actions.CANCELLED') : t(`demandes.actions.${status}`)}
                          >
                            {status === 'CANCELLED' ? <XCircle size={16} /> : <CheckCircle size={16} />}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {(viewMode === 'list' || listType === 'restock') ? (
        (listType !== 'restock') && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('sessions.table.formation')}</th>
                <th>{t('sessions.table.instructor')}</th>
                <th>{t('sessions.table.createdBy')}</th>
                <th>{t('sessions.table.dates')}</th>
                <th>{t('sessions.table.students')}</th>
                <th>{t('sessions.table.status')}</th>
                <th>{t('sessions.table.budget')}</th>
                <th className="actions-column">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredSessions.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>
                    {t('sessions.empty')}
                  </td>
                </tr>
              ) : (
                filteredSessions.map((session) => (
                  <tr key={session.id}>
                    <td className="name-cell">{session.formation_title}</td>
                    <td>
                      <div className="trainer-cell">
                        <User size={14} />
                        <span>{getInstructorName(session)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="trainer-cell">
                        <User size={14} />
                        <span>{getCreatorName(session)}</span>
                      </div>
                    </td>
                    <td>
                      <div>
                        {new Date(session.start_date).toLocaleDateString(i18n.language)}
                        {session.end_date ? ` → ${new Date(session.end_date).toLocaleDateString(i18n.language)}` : ''}
                      </div>
                    </td>
                    <td>
                      <div className="participants-cell">
                        <UsersIcon size={14} />
                        <span>{session.student_count || 0}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusClass(session.status)}`}>
                        {t(`status.session.${session.status}`)}
                      </span>
                    </td>
                    <td>
                      <div className="budget-cell">
                        <CircleDollarSign size={14} />
                        {session.budget_totals_by_currency && Object.keys(session.budget_totals_by_currency).length > 0 ? (
                          Object.entries(session.budget_totals_by_currency).map(([currency, amount], idx) => (
                            <span key={currency} style={{ display: 'block' }}>
                              {toMoney(amount, { centre_devise: currency })}
                            </span>
                          ))
                        ) : (
                          <span>{session.total_budget}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <Link to={`/sessions/${session.id}`} className="action-btn" title={t('common.view')}>
                          <Eye size={16} />
                        </Link>
                        <Link
                          to={`/sessions/${session.id}#additional-fees`}
                          className="action-btn"
                          title="Frais additionnels"
                        >
                          <Receipt size={16} />
                        </Link>
                        {((!isResponsable && canEditSession) || (isResponsable && ['DEMANDE_MODIF', 'DEMANDE_MODIF_MANAGER'].includes(session.status))) && (
                          <button
                            type="button"
                            className="action-btn"
                            onClick={() => handleEditClick(session)}
                            title={t('common.edit')}
                          >
                            <Edit size={16} />
                          </button>
                        )}
                        {canCancelRequestedSession && isResponsable && !['ANNULEE', 'VALIDEE', 'REFUSEE'].includes(session.status) && (
                          <button
                            type="button"
                            className="action-btn"
                            onClick={() => handleCancelSession(session.id)}
                            title={t('status.session.ANNULEE')}
                          >
                            <XCircle size={16} />
                          </button>
                        )}
                        {canDelete('sessions') && !isResponsable && (
                          <button
                            type="button"
                            className="action-btn action-delete"
                            onClick={() => handleDeleteSession(session.id)}
                            title={t('common.delete')}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        )
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="kanban-board">
            {columns.map((col) => {
              const ColumnIcon = col.icon;
              const colSessions = sessionsByStatus[col.id] || [];
              const canDragDrop = canEdit('sessions');
              return (
                <Droppable
                  key={col.id}
                  droppableId={col.id}
                  isDropDisabled={!canDragDrop}
                >
                  {(provided, snapshot) => (
                    <div className={`kanban-column ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}>
                      <div
                        className="kanban-column-header"
                        style={{ borderColor: col.color, borderLeftColor: col.color }}
                      >
                        <div className="column-title">
                          <ColumnIcon size={16} style={{ color: col.color }} />
                          <span>{col.label}</span>
                        </div>
                        <span className="column-count">{colSessions.length}</span>
                      </div>

                      <div
                        className="column-content"
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {colSessions.map((session, index) => (
                          <Draggable
                            key={session.id}
                            draggableId={session.id}
                            index={index}
                            isDragDisabled={!canDragDrop}
                          >
                            {(provided) => (
                              <div
                                className="session-card"
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <div className="card-header">
                                  <h4 className="formation-title">{session.formation_title}</h4>
                                  <button className="card-menu" type="button" aria-label={t('common.actions')}>
                                    <MoreVertical size={16} />
                                  </button>
                                </div>
                                <div className="card-trainer">
                                  <User size={14} />
                                  <span>{t('sessions.table.instructor')}: {getInstructorName(session)}</span>
                                </div>
                                <div className="card-trainer">
                                  <User size={14} />
                                  <span>{t('sessions.table.createdBy')}: {getCreatorName(session)}</span>
                                </div>
                                <div className="card-meta">
                                  <div className="meta-item">
                                    <UsersIcon size={14} />
                                    <span>{t('dashboard.participantsCount', { count: session.student_count || 0 })}</span>
                                  </div>
                                </div>
                                <div className="card-budget">
                                  <CircleDollarSign size={14} />
                                  {session.budget_totals_by_currency && Object.keys(session.budget_totals_by_currency).length > 0 ? (
                                    Object.entries(session.budget_totals_by_currency).map(([currency, amount], idx) => (
                                      <span key={currency} style={{ display: 'block' }}>
                                        {toMoney(amount, { centre_devise: currency })}
                                      </span>
                                    ))
                                  ) : (
                                    <span>{session.total_budget}</span>
                                  )}
                                </div>
                                <div className="card-date">
                                  <Calendar size={12} />
                                  <span>{new Date(session.start_date).toLocaleDateString(i18n.language)}</span>
                                </div>
                                <div className="card-actions">
                                  <Link to={`/sessions/${session.id}`} className="card-action-btn view" title={t('common.view')}>
                                    <Eye size={14} />
                                  </Link>
                                  <Link
                                    to={`/sessions/${session.id}#additional-fees`}
                                    className="card-action-btn view"
                                    title="Frais additionnels"
                                  >
                                    <Receipt size={14} />
                                  </Link>
                                  {((!isResponsable && canEditSession) || (isResponsable && ['DEMANDE_MODIF', 'DEMANDE_MODIF_MANAGER'].includes(session.status))) && (
                                    <>
                                      <button
                                        className="card-action-btn edit"
                                        onClick={() => handleEditClick(session)}
                                        type="button"
                                        title={t('common.edit')}
                                      >
                                        <Edit size={14} />
                                      </button>
                                    </>
                                  )}
                                  {canCancelRequestedSession && isResponsable && !['ANNULEE', 'VALIDEE', 'REFUSEE'].includes(session.status) && (
                                    <button
                                      className="card-action-btn delete"
                                      type="button"
                                      onClick={() => handleCancelSession(session.id)}
                                      title={t('status.session.ANNULEE')}
                                    >
                                      <XCircle size={14} />
                                    </button>
                                  )}
                                  {canDelete('sessions') && !isResponsable && (
                                    <button
                                      className="card-action-btn delete"
                                      type="button"
                                      onClick={() => handleDeleteSession(session.id)}
                                      title={t('common.delete')}
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {colSessions.length === 0 && (
                          <div className="kanban-empty">{t('sessions.empty')}</div>
                        )}
                      </div>
                    </div>
                  )}
                </Droppable>
              );
            })}
          </div>
        </DragDropContext>
      )}

      <div className="sessions-mobile">
        <div className="mobile-view-toggle">
          <button
            type="button"
            className={`mobile-view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            <List size={16} />
            <span>{t('common.list') || 'Liste'}</span>
          </button>
          <button
            type="button"
            className={`mobile-view-toggle-btn ${viewMode === 'kanban' ? 'active' : ''}`}
            onClick={() => setViewMode('kanban')}
          >
            <LayoutGrid size={16} />
            <span>{t('common.kanban') || 'Kanban'}</span>
          </button>
        </div>

        {viewMode === 'list' ? (
          <div className="sessions-mobile-list">
            {filteredSessions.length === 0 ? (
              <div className="kanban-empty">{t('sessions.empty')}</div>
            ) : (
              filteredSessions.map((session) => (
                <div key={session.id} className="session-card">
                  <div className="card-header">
                    <h4 className="formation-title">{session.formation_title}</h4>
                    <span className={`status-badge ${getStatusClass(session.status)}`}>
                      {t(`status.session.${session.status}`)}
                    </span>
                  </div>
                  <div className="card-trainer">
                    <User size={14} />
                    <span>{t('sessions.table.instructor')}: {getInstructorName(session)}</span>
                  </div>
                  <div className="card-trainer">
                    <User size={14} />
                    <span>{t('sessions.table.createdBy')}: {getCreatorName(session)}</span>
                  </div>
                  <div className="card-meta">
                    <div className="meta-item">
                      <UsersIcon size={14} />
                      <span>{t('dashboard.participantsCount', { count: session.student_count || 0 })}</span>
                    </div>
                    <div className="meta-item budget">
                      <CircleDollarSign size={14} />
                      <span>{session.total_budget}</span>
                    </div>
                  </div>
                  <div className="card-date">
                    <Calendar size={12} />
                    <span>{new Date(session.start_date).toLocaleDateString(i18n.language)}</span>
                  </div>
                  <div className="card-actions">
                    <Link to={`/sessions/${session.id}`} className="card-action-btn view">
                      <Eye size={14} />
                    </Link>
                    <Link to={`/sessions/${session.id}#additional-fees`} className="card-action-btn view" title="Frais additionnels">
                      <Receipt size={14} />
                    </Link>
                    {canCancelRequestedSession && isResponsable && !['ANNULEE', 'VALIDEE', 'REFUSEE'].includes(session.status) && (
                      <button
                        className="card-action-btn delete"
                        type="button"
                        onClick={() => handleCancelSession(session.id)}
                        title={t('status.session.ANNULEE')}
                      >
                        <XCircle size={14} />
                      </button>
                    )}
                    {canDelete('sessions') && !isResponsable && (
                      <button
                        className="card-action-btn delete"
                        type="button"
                        onClick={() => handleDeleteSession(session.id)}
                        title={t('common.delete')}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                    {((!isResponsable && canEditSession) || (isResponsable && ['DEMANDE_MODIF', 'DEMANDE_MODIF_MANAGER'].includes(session.status))) && (
                      <button
                        className="card-action-btn edit"
                        onClick={() => handleEditClick(session)}
                        type="button"
                        title={t('common.edit')}
                      >
                        <Edit size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <>
            <div className="sessions-status-tabs">
              {columns.map((col) => {
                const ColumnIcon = col.icon;
                const count = (sessionsByStatus[col.id] || []).length;
                const isActive = activeMobileStatus === col.id;
                return (
                  <button
                    key={col.id}
                    type="button"
                    className={`sessions-status-tab ${isActive ? 'active' : ''}`}
                    onClick={() => setActiveMobileStatus(col.id)}
                  >
                    <ColumnIcon size={16} style={{ color: col.color }} />
                    <span className="tab-label">{col.label}</span>
                    <span className="tab-count">{count}</span>
                  </button>
                );
              })}
            </div>

            <div className="sessions-mobile-list">
              {mobileSessions.length === 0 ? (
                <div className="kanban-empty">{t('sessions.empty')}</div>
              ) : (
                mobileSessions.map((session) => (
                  <div key={session.id} className="session-card">
                    <div className="card-header">
                      <h4 className="formation-title">{session.formation_title}</h4>
                      <button className="card-menu" type="button">
                        <MoreVertical size={16} />
                      </button>
                    </div>
                    <div className="card-trainer">
                      <User size={14} />
                      <span>{t('sessions.table.instructor')}: {getInstructorName(session)}</span>
                    </div>
                    <div className="card-trainer">
                      <User size={14} />
                      <span>{t('sessions.table.createdBy')}: {getCreatorName(session)}</span>
                    </div>
                    <div className="card-meta">
                      <div className="meta-item">
                        <UsersIcon size={14} />
                        <span>{t('dashboard.participantsCount', { count: session.student_count || 0 })}</span>
                      </div>
                      <div className="meta-item budget">
                        <CircleDollarSign size={14} />
                        <span>{session.total_budget}</span>
                      </div>
                    </div>
                    <div className="card-date">
                      <Calendar size={12} />
                      <span>{new Date(session.start_date).toLocaleDateString(i18n.language)}</span>
                    </div>
                    <div className="card-actions">
                      <Link to={`/sessions/${session.id}`} className="card-action-btn view">
                        <Eye size={14} />
                      </Link>
                      <Link to={`/sessions/${session.id}#additional-fees`} className="card-action-btn view" title="Frais additionnels">
                        <Receipt size={14} />
                      </Link>
                      {canCancelRequestedSession && isResponsable && !['ANNULEE', 'VALIDEE', 'REFUSEE'].includes(session.status) && (
                        <button
                          className="card-action-btn delete"
                          type="button"
                          onClick={() => handleCancelSession(session.id)}
                          title={t('status.session.ANNULEE')}
                        >
                          <XCircle size={14} />
                        </button>
                      )}
                      {canDelete('sessions') && !isResponsable && (
                        <button
                          className="card-action-btn delete"
                          type="button"
                          onClick={() => handleDeleteSession(session.id)}
                          title={t('common.delete')}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                      {canCancelRequestedSession && isResponsable && session.status === 'ANNULEE' && (
                        <button
                          className="card-action-btn edit"
                          type="button"
                          onClick={async () => {
                            try {
                              await sessionService.reopenToVerification(session.id);
                              fetchSessions(currentPage);
                              toast.success(t('status.session.VERIFICATION'));
                            } catch (e) {
                              toast.error(t('sessions.detail.errors.statusChange'));
                            }
                          }}
                          title={t('status.session.VERIFICATION')}
                        >
                          <Clock size={14} />
                        </button>
                      )}
                      {((!isResponsable && canEditSession) || (isResponsable && ['DEMANDE_MODIF', 'DEMANDE_MODIF_MANAGER'].includes(session.status))) && (
                        <button
                          className="card-action-btn edit"
                          onClick={() => handleEditClick(session)}
                          type="button"
                          title={t('common.edit')}
                        >
                          <Edit size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>


      <div className="table-footer">
        <p className="results-count">
          {t('sessions.resultsCount', { total: totalCount, currentPage, totalPages })}
        </p>
        <div className="pagination">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            {t('common.previous')}
          </button>
          <div className="page-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`page-number ${page === currentPage ? 'active' : ''}`}
              >
                {page}
              </button>
            ))}
          </div>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            {t('common.next')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionsPage;
